import apiClient from './client';

export interface PendingSessionInfo {
  sessionId: string;
  status: string;
  proposedTime?: string | null;
  pendingDescription: string;
  waitingForCurrentUser: boolean;
  // Extended fields for quick actions
  proposalId?: string | null;
  proposedBy?: 'STUDENT' | 'TUTOR' | null;
  duration?: number | null;
  subject?: string;
  otherPartyName?: string;
}

export interface ConfirmedSessionInfo {
  sessionId: string;
  scheduledTime: string;
  timeDescription: string;
  duration?: number | null;
  subject?: string;
  otherPartyName?: string;
}

export interface ChatChannel {
  id: string;
  name: string | null;
  type: 'PRIVATE' | 'GROUP';
  memberCount: number;
  createdAt: string;
  unreadCount?: number;
  lastMessageAt?: string;
  lastReadAt?: string;
  hasUnread?: boolean;
  lastMessageContent?: string | null;
  lastMessageSenderName?: string | null;
  // Multiple confirmed sessions
  hasConfirmedSessions?: boolean;
  confirmedSessions?: ConfirmedSessionInfo[];
  // Legacy single session fields (for backward compatibility)
  isPinned?: boolean;
  pinnedSessionId?: string;
  sessionStartTime?: string;
  sessionTimeDescription?: string;
  // Pending sessions (multiple)
  hasPendingSessions?: boolean;
  pendingSessions?: PendingSessionInfo[];
  directMessageAvatarUrl?: string | null;
}

export interface ChatMessage {
  id: string;
  channelId: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
  fromCurrentUser: boolean;
  messageType: 'USER' | 'SYSTEM';
  attachment?: StoredFile;
}

export interface StoredFile {
  id: string;
  originalFilename: string;
  storageKey: string;
  contentType: string;
  sizeBytes: number;
  uploadStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatUserResult {
  id: string;
  fullName: string;
  email: string;
}

export interface ChatChannelMember {
  userId: string;
  fullName: string;
  email: string;
  role: 'OWNER' | 'MEMBER';
}

export const chatApi = {
  listChannels: async (): Promise<ChatChannel[]> => {
    const res = await apiClient.get('/chat/channels');
    return res.data;
  },
  createChannel: async (payload: { type: 'PRIVATE' | 'GROUP'; name?: string; memberIds?: string[] }): Promise<ChatChannel> => {
    const res = await apiClient.post('/chat/channels', payload);
    return res.data;
  },
  getMessages: async (channelId: string, before?: string, limit: number = 50): Promise<ChatMessage[]> => {
    const params: Record<string, string> = { limit: limit.toString() };
    if (before) {
      params.before = before;
    }
    const res = await apiClient.get(`/chat/channels/${channelId}/messages`, { params });
    return res.data;
  },
  sendMessage: async (channelId: string, content: string): Promise<ChatMessage> => {
    const res = await apiClient.post(`/chat/channels/${channelId}/messages`, { content });
    return res.data;
  },
  searchUsers: async (query: string): Promise<ChatUserResult[]> => {
    const res = await apiClient.get('/chat/users', { params: { q: query } });
    return res.data;
  },
  markChannelAsRead: async (channelId: string): Promise<void> => {
    await apiClient.post(`/chat/channels/${channelId}/mark-read`);
  },
  getChannelReadStatus: async (channelId: string): Promise<{ lastReadAt: string | null; unreadCount: number }> => {
    const res = await apiClient.get(`/chat/channels/${channelId}/read-status`);
    return res.data;
  },
  getChannelMembers: async (channelId: string): Promise<ChatChannelMember[]> => {
    const res = await apiClient.get(`/chat/channels/members/getMembers`, { params: { channelId } });
    return res.data;
  },
  addMember: async (channelId: string, userId: string): Promise<void> => {
    await apiClient.post(`/chat/channels/members/addMember`, { channelId, userId });
  },
  removeMember: async (channelId: string, userId: string): Promise<void> => {
    await apiClient.post(`/chat/channels/members/removeMember`, { channelId, userId });
  },
  uploadFile: async (channelId: string, file: File, message?: string): Promise<ChatMessage> => {
    const formData = new FormData();
    formData.append('file', file);
    if (message) {
      formData.append('message', message);
    }
    // Don't set Content-Type header - let the browser set it with the boundary
    const res = await apiClient.post(`/chat/channels/${channelId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  },
  downloadFile: async (fileId: string): Promise<Blob> => {
    const res = await apiClient.get(`/files/${fileId}`, {
      responseType: 'blob',
    });
    return res.data;
  },
};
